import { BreadcrumbOptions } from "@/components/breadcrumbs";
import { DomainList, DomainListItemCard } from "@/components/domain";
import { Skeleton } from "@/components/ui/skeleton";
import { useDeletetourAgency, useGetTourAgencies } from "@/hooks/tour-agencies";
import { T } from "@/i18n";
import { TourAgency, TourAgencyDomain } from "@/models";
import { Building2, Waypoints } from "lucide-react";
import { Key, ReactNode } from "react";
import { useTranslation } from "react-i18next";

function getBreadCrumbsForAgency(
  name: string,
  id: string,
  loading: boolean,
): BreadcrumbOptions[] {
  return [
    {
      content: (
        <>
          <T tKey="agencies.title" /> <Building2 width={16} height={16} />
        </>
      ),
      href: "/agencies",
    },
    {
      content: (
        <>
          {loading ? (
            <Skeleton className="h-5 w-10" />
          ) : (
            <>
              {name} <Building2 width={16} height={16} />
            </>
          )}
        </>
      ),
      href: "/agencies/$id",
      params: { id },
    },
    {
      content: (
        <>
          <T tKey="tours.title" /> <Waypoints width={16} height={16} />
        </>
      ),
      href: "/tours",
    },
  ];
}

function getBreadCrumbsForTour(
  name: string,
  id: string,
  loading: boolean,
): BreadcrumbOptions[] {
  return [
    {
      content: (
        <>
          <T tKey="tours.title" /> <Waypoints width={16} height={16} />
        </>
      ),
      href: "/tours",
    },
    {
      content: (
        <>
          {loading ? (
            <Skeleton className="h-5 w-10" />
          ) : (
            <>
              {name}
              <Waypoints width={16} height={16} />
            </>
          )}
        </>
      ),
      href: "/tours/$id",
      params: { id },
    },
    {
      content: (
        <>
          <T tKey="agencies.title" /> <Building2 width={16} height={16} />
        </>
      ),
      href: "/agencies",
    },
  ];
}

const breadcrumbsForDomain: Record<
  TourAgencyDomain,
  (name: string, id: string, loading: boolean) => BreadcrumbOptions[]
> = {
  agency: getBreadCrumbsForAgency,
  tour: getBreadCrumbsForTour,
};

type LinkFn = (id: string, children: ReactNode, key: Key) => JSX.Element;

export function TourAgenciesPage({
  domain,
  domainId,
  domainLoading,
  domainName,
  editLink,
}: {
  domain: TourAgencyDomain;
  domainId: string;
  domainName?: string;
  domainLoading: boolean;
  editLink: LinkFn;
}) {
  const query = useGetTourAgencies(domain, domainId);

  return (
    <DomainList
      domain={`tourAgencies.${domain}`}
      breadcrumbs={breadcrumbsForDomain[domain](
        domainName ?? "",
        domainId,
        domainLoading,
      )}
      tableOptions={{
        hasActions: true,
        columns: [
          {
            header: "tourAgencies.fields.alias",
          },
          {
            header:
              domain === "tour" ? "agencies.fields.name" : "tours.fields.name",
          },
          {
            header: "tourAgencies.fields.priceCommission",
          },
          {
            header: "tourAgencies.fields.seasonSupplementCommission",
          },
          {
            header: "tourAgencies.fields.individualRoomSupplementCommission",
          },
          {
            header: "tourAgencies.fields.soloTravelerCommission",
          },
          {
            header: "tourAgencies.fields.bikesCommissions",
          },
          {
            header: "tourAgencies.fields.extraNightsCommissions",
          },
          {
            header: "tourAgencies.fields.roomChangeCommissions",
          },
          {
            header: "tourAgencies.fields.servicesCommissions",
          },
        ],
      }}
      type="table"
      data={query.data}
      isLoading={query.isLoading}
      newLink="/tour-agencies/$id/new"
      newLinkParams={{ id: domainId }}
      newLinkSearch={{ domain }}
      renderItem={(tourAgency, index, isTable) => (
        <TourAgencyCard
          isTable={isTable}
          editLink={editLink}
          domain={domain}
          domainId={domainId}
          tourAgency={tourAgency}
          key={index}
          name={
            domain === "tour"
              ? (tourAgency.agency?.name ??
                tourAgency.agencyId?.toString() ??
                "")
              : (tourAgency.tour?.name ?? tourAgency.tourId?.toString() ?? "")
          }
        />
      )}
    />
  );
}

function TourAgencyCard({
  tourAgency,
  name,
  domain,
  domainId,
  editLink,
  isTable,
}: {
  tourAgency: TourAgency;
  name: string;
  domain: TourAgencyDomain;
  domainId: string;
  editLink: LinkFn;
  isTable: boolean;
}) {
  const { mutateAsync } = useDeletetourAgency(domain, domainId);
  const deleteTourAgency = () => mutateAsync(tourAgency.id.toString());
  const { t } = useTranslation();
  return (
    <DomainListItemCard
      table={isTable}
      domain={`tourAgencies.${domain}`}
      title={tourAgency.alias || "-"}
      titleTableOptions={{
        tableClassName: "whitespace-nowrap",
      }}
      description={name}
      descriptionTableOptions={{
        tableClassName: "whitespace-nowrap",
      }}
      editLink={(children, key) => editLink(tourAgency.id, children, key)}
      contentMap={[
        {
          label: t("tourAgencies.fields.priceCommission"),
          value: tourAgency.priceCommission ?? "-",
        },
        {
          label: t("tourAgencies.fields.seasonSupplementCommission"),
          value: tourAgency.seasonSupplementCommission ?? "-",
        },
        {
          label: t("tourAgencies.fields.individualRoomSupplementCommission"),
          value: tourAgency.individualRoomSupplementCommission ?? "-",
        },
        {
          label: t("tourAgencies.fields.soloTravelerCommission"),
          value: tourAgency.soloTravelerCommission ?? "-",
        },
        {
          label: t("tourAgencies.fields.bikesCommissions"),
          value: tourAgency.bikesCommissions ?? "-",
        },
        {
          label: t("tourAgencies.fields.extraNightsCommissions"),
          value: tourAgency.extraNightsCommissions ?? "-",
        },
        {
          label: t("tourAgencies.fields.roomChangeCommissions"),
          value: tourAgency.roomChangeCommissions ?? "-",
        },
        {
          label: t("tourAgencies.fields.servicesCommissions"),
          value: tourAgency.servicesCommissions ?? "-",
        },
      ]}
      onDelete={deleteTourAgency}
    />
  );
}
